<template>
  <v-container>
    <v-card :loading="loading">
      <v-card-title>
        <span v-if="ticket">#{{ ticket.id }} - {{ ticket.title }}</span>
        <v-spacer></v-spacer>
        <v-btn text :to="{ name: routeName.SUPPORT_LIST }">
          <v-icon left>arrow_back</v-icon>
          {{ $t('BACK') }}
        </v-btn>
      </v-card-title>

      <v-card-text v-if="ticket">
        <v-row>
          <v-col cols="12" md="3">
            <div class="caption grey--text">{{ $t('SUPPORT_STATUS') }}</div>
            <v-chip small :color="statusColor(ticket.status)" text-color="white">
              {{ ticket.status_label }}
            </v-chip>
          </v-col>
          <v-col cols="12" md="3">
            <div class="caption grey--text">{{ $t('SUPPORT_AREA') }}</div>
            <div>{{ ticket.area_label }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="caption grey--text">{{ $t('SUPPORT_TYPE') }}</div>
            <div>{{ ticket.type_label }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="caption grey--text">{{ $t('SUPPORT_SEVERITY') }}</div>
            <div>{{ ticket.severity_label }}</div>
          </v-col>

          <v-col cols="12" md="4" v-if="ticket.user">
            <div class="caption grey--text">{{ $t('SUPPORT_SENT_BY') }}</div>
            <div>{{ ticket.user.name }} ({{ ticket.user.email }})</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="caption grey--text">{{ $t('SUPPORT_CREATED_AT') }}</div>
            <div>{{ formatDate(ticket.created_at) }}</div>
          </v-col>
          <v-col cols="12" md="4" v-if="ticket.page_url">
            <div class="caption grey--text">{{ $t('SUPPORT_PAGE_URL') }}</div>
            <div>{{ ticket.page_url }}</div>
          </v-col>

          <v-col cols="12">
            <div class="caption grey--text">{{ $t('SUPPORT_DESCRIPTION') }}</div>
            <div class="body-1 support-text">{{ ticket.description }}</div>
          </v-col>

          <v-col cols="12" md="6" v-if="ticket.steps">
            <div class="caption grey--text">{{ $t('SUPPORT_STEPS') }}</div>
            <div class="body-1 support-text">{{ ticket.steps }}</div>
          </v-col>

          <v-col cols="12" md="6" v-if="ticket.expected">
            <div class="caption grey--text">{{ $t('SUPPORT_EXPECTED') }}</div>
            <div class="body-1 support-text">{{ ticket.expected }}</div>
          </v-col>

          <v-col cols="12" v-if="ticket.context">
            <div class="caption grey--text">{{ $t('SUPPORT_TECH_INFO') }}</div>
            <div class="caption">
              <span v-if="ticket.context.app_version">
                {{ $t('VERSION') }} {{ ticket.context.app_version }} &middot;
              </span>
              <span v-if="ticket.context.screen">{{ ticket.context.screen }} &middot; </span>
              <span v-if="ticket.context.user_agent">{{ ticket.context.user_agent }}</span>
            </div>
          </v-col>

          <v-col cols="12" v-if="ticket.files && ticket.files.length">
            <div class="caption grey--text mb-2">{{ $t('SUPPORT_ATTACHMENTS') }}</div>
            <v-row>
              <v-col
                cols="12"
                md="4"
                v-for="file in ticket.files"
                :key="file.id"
              >
                <v-card outlined>
                  <img
                    v-if="file.is_image && previews[file.id]"
                    :src="previews[file.id]"
                    class="support-preview"
                  />
                  <video
                    v-else-if="file.is_video && previews[file.id]"
                    :src="previews[file.id]"
                    class="support-preview"
                    controls
                  ></video>
                  <v-card-text class="py-2 caption">{{ file.fullname }}</v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" v-if="canManage">
            <v-divider class="mb-4"></v-divider>
            <div class="subtitle-1 mb-2">{{ $t('SUPPORT_MANAGE') }}</div>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="statusForm.status"
                  :items="statusOptions"
                  item-text="label"
                  item-value="value"
                  :label="$t('SUPPORT_STATUS')"
                ></v-select>
              </v-col>
              <v-col cols="12" md="8">
                <v-textarea
                  v-model="statusForm.admin_note"
                  rows="2"
                  :label="$t('SUPPORT_ADMIN_NOTE')"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-btn color="primary" :loading="saving" @click="saveStatus">
              {{ $t('SAVE') }}
            </v-btn>
          </v-col>

          <v-col cols="12" v-else-if="ticket.admin_note">
            <div class="caption grey--text">{{ $t('SUPPORT_ADMIN_NOTE') }}</div>
            <div class="body-1 support-text">{{ ticket.admin_note }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

import { supportService } from '../../utils/services/support.service';

export default {
  name: 'SupportShow',
  props: {
    id: [String, Number],
  },
  data() {
    return {
      loading: false,
      saving: false,
      ticket: null,
      previews: {},
      statusOptions: [],
      statusForm: {
        status: null,
        admin_note: '',
      },
    };
  },
  computed: {
    canManage() {
      return this.can('manage support');
    },
  },
  created() {
    this.fetchTicket();
    if (this.can('manage support')) {
      this.fetchOptions();
    }
  },
  beforeDestroy() {
    Object.keys(this.previews).forEach(key => {
      URL.revokeObjectURL(this.previews[key]);
    });
  },
  methods: {
    ...mapActions('alert', ['showToast']),

    async fetchTicket() {
      this.loading = true;
      try {
        const response = await supportService.show(this.id);
        this.ticket = response.data.data;
        this.statusForm.status = this.ticket.status;
        this.statusForm.admin_note = this.ticket.admin_note || '';
        this.loadPreviews();
      } catch (error) {
        this.handleError(error, this.errors);
      } finally {
        this.loading = false;
      }
    },

    async fetchOptions() {
      try {
        const response = await supportService.getOptions();
        this.statusOptions = response.data.statuses;
      } catch (error) {
        this.handleError(error, this.errors);
      }
    },

    loadPreviews() {
      if (!this.ticket || !this.ticket.files) {
        return;
      }
      this.ticket.files.forEach(file => {
        supportService.downloadAttachment(file.id)
          .then(response => {
            const url = URL.createObjectURL(new Blob([response.data], { type: file.content_type }));
            this.$set(this.previews, file.id, url);
          })
          .catch(() => {
            // Anteprima non disponibile: il dettaglio resta comunque leggibile.
          });
      });
    },

    async saveStatus() {
      this.saving = true;
      try {
        await supportService.updateStatus(this.id, {
          status: this.statusForm.status,
          admin_note: this.statusForm.admin_note || null,
        });
        this.showToast({ type: 'success', message: this.$t('SAVED') });
        this.$router.push({ name: this.routeName.SUPPORT_LIST });
      } catch (error) {
        this.handleError(error, this.errors);
        this.saving = false;
      }
    },

    statusColor(status) {
      if (status === 'OPEN') return 'red darken-1';
      if (status === 'IN_PROGRESS') return 'orange darken-2';
      if (status === 'RESOLVED') return 'green darken-1';
      return 'grey darken-1';
    },

    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('it-IT');
    },
  },
};
</script>

<style scoped>
.support-text {
  white-space: pre-wrap;
}
.support-preview {
  width: 100%;
  max-height: 260px;
  object-fit: contain;
  background: #000;
}
</style>
